export const getPasswordRequirements = (password) => ({
  hasUpper: /[A-Z]/.test(password),
  hasLower: /[a-z]/.test(password),
  hasNumberOrSpecial: /(?:\d|[^A-Za-z0-9\s])/.test(password),
  isMinLength: password.length >= 8,
});

export const getStrengthConfig = ({ strengthScore, t }) => {
  if (strengthScore === null) {
    return {
      color: "text.secondary",
      label: t("password.checking"),
      value: 0,
    };
  }

  switch (strengthScore) {
    case 0:
      return {
        color: "text.secondary",
        label: t("password.veryWeak"),
        value: 0,
      };
    case 1:
      return {
        color: "error.main",
        label: t("password.weak"),
        value: 25,
      };
    case 2:
      return {
        color: "warning.main",
        label: t("password.fair"),
        value: 50,
      };
    case 3:
      return {
        color: "success.light",
        label: t("password.good"),
        value: 75,
      };
    case 4:
      return {
        color: "success.main",
        label: t("password.strong"),
        value: 100,
      };
    default:
      return {
        color: "text.secondary",
        label: t("password.checking"),
        value: 0,
      };
  }
};

// Lazy load zxcvbn to optimize initial load time, since it's a large library and only needed when users interact with password fields.
let zxcvbnPromise = null;

const loadZxcvbn = async () => {
  if (!zxcvbnPromise) {
    zxcvbnPromise = (async () => {
      try {
        const [{ zxcvbn, zxcvbnOptions }, common, en] = await Promise.all([
          import("@zxcvbn-ts/core"),
          import("@zxcvbn-ts/language-common"),
          import("@zxcvbn-ts/language-en"),
        ]);

        zxcvbnOptions.setOptions({
          dictionary: {
            ...common.dictionary,
            ...en.dictionary,
          },
          graphs: common.adjacencyGraphs,
          translations: en.translations,
        });

        return zxcvbn;
      } catch (error) {
        zxcvbnPromise = null;
        throw error;
      }
    })();
  }

  return zxcvbnPromise;
};

export const getPasswordStrength = async (password) => {
  const zxcvbn = await loadZxcvbn();
  return zxcvbn(password);
};
